import type { ApiError, AuthTokens, Page } from '@cabgo/shared';

/**
 * Fetch client — Bearer auth, single-flight refresh on 401, standard
 * error envelope. Tokens persist in localStorage (SPA; stateless API).
 */
export const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== 'undefined' && window.location.origin.includes('getbb.app')
    ? window.location.origin.replace('--5173', '--3000')
    : 'http://localhost:3000');
const TOKEN_KEY = 'cabgo.tokens';
const ACTIVE_ORG_KEY = 'cabgo.activeOrg';

/** SUPER_ADMIN org switcher — selected org id, sent as X-Org-Id on every call. */
export function getActiveOrg(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

export function setActiveOrg(orgId: string | null) {
  try {
    if (orgId) localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    else localStorage.removeItem(ACTIVE_ORG_KEY);
  } catch {}
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('cabgo:scope-changed'));
  }
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  /** Envelope `error.details` (e.g. `missingSkills` on the MANUAL skill-gate 403). */
  readonly details?: Record<string, unknown>;
  constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function isAccessDeniedError(error: unknown): boolean {
  return error instanceof ApiRequestError && [401, 403, 404].includes(error.status);
}

export function getTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthTokens;
  } catch {
    return null;
  }
}

export function setTokens(tokens: AuthTokens | null) {
  try {
    if (tokens) localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

/** Bearer + org-switcher headers shared by every fetch wrapper in this module. */
function authHeaders(): Record<string, string> {
  const tokens = getTokens();
  const activeOrg = getActiveOrg();
  return {
    ...(tokens ? { authorization: `Bearer ${tokens.accessToken}` } : {}),
    // ignored by the API unless the caller is SUPER_ADMIN
    ...(activeOrg ? { 'x-org-id': activeOrg } : {}),
  };
}

/** Standard error envelope → ApiRequestError, shared by api() and apiDownload(). */
function errorFromEnvelope(body: ApiError | null, res: Response): ApiRequestError {
  const err = body?.error;
  return new ApiRequestError(
    err?.code ?? 'UNKNOWN',
    err?.message ?? res.statusText,
    res.status,
    err?.details,
  );
}

/**
 * Cross-module contract: fired here, listened to in lib/auth.tsx. A single
 * exported constant so the two ends (and tests) can never drift apart —
 * TypeScript can't catch a mismatch between duplicated string literals.
 */
export const SESSION_EXPIRED_EVENT = 'cabgo:session-expired';

/**
 * Unrecoverable mid-session expiry: refresh failed or the refreshed token was
 * rejected again. Clear the session and tell AuthProvider, which drops `me` so
 * RequireAuth bounces the operator to /login instead of leaving them on a page
 * full of failed queries.
 */
function expireSession(): void {
  setTokens(null);
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

let refreshing: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  refreshing ??= (async () => {
    const tokens = getTokens();
    if (!tokens) return false;
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
      if (!res.ok) {
        setTokens(null);
        return false;
      }
      setTokens((await res.json()) as AuthTokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const res = await fetch(`${API_URL}/api/v1${path}`, {
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 401 && getTokens() && !path.startsWith('/auth/')) {
    if (!retried && (await refreshTokens())) return api<T>(path, init, true);
    expireSession();
  }

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => null)) as (ApiError & Record<string, unknown>) | null;
  if (!res.ok) throw errorFromEnvelope(body, res);
  return body as T;
}

/**
 * Build an absolute WebSocket URL for an `/api/v1` path (http→ws / https→wss),
 * with the access token appended as a query param — the API authenticates the
 * upgrade via `?token=` since browsers can't set headers on a WS handshake.
 */
export function wsUrl(path: string, selectedOrg: string | null = getActiveOrg()): string {
  const base = API_URL.replace(/^http/, 'ws');
  const token = getTokens()?.accessToken ?? '';
  const sep = path.includes('?') ? '&' : '?';
  return `${base}/api/v1${path}${sep}token=${encodeURIComponent(token)}${selectedOrg ? `&orgId=${encodeURIComponent(selectedOrg)}` : ''}`;
}

/**
 * Fetch a file endpoint (e.g. the analytics CSV export) with the usual auth
 * headers and hand it to the browser as a download. Filename comes from the
 * server's content-disposition. Shares api()'s 401 refresh + session-expiry
 * handling so a download never diverges from the session contract.
 */
export async function apiDownload(
  path: string,
  headers: Record<string, string> = {},
  retried = false,
): Promise<void> {
  const res = await fetch(`${API_URL}/api/v1${path}`, { headers: { ...authHeaders(), ...headers } });
  if (res.status === 401 && getTokens()) {
    if (!retried && (await refreshTokens())) return apiDownload(path, headers, true);
    expireSession();
  }
  if (!res.ok) {
    throw errorFromEnvelope((await res.json().catch(() => null)) as ApiError | null, res);
  }
  const name = res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'export.csv';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Unauthenticated POST for the public (logged-out) pages — same base URL and
 * error-envelope contract as api(), but never sends auth headers and never
 * runs the 401-refresh machinery: on a public page a 401 is a business answer
 * (e.g. a wrong OTP), and the refresh path would burn a logged-in operator's
 * session in the same browser. Throws ApiRequestError (carries `.code`).
 */
export async function publicPost<T = void>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_URL}/api/v1${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const responseBody = (await res.json().catch(() => null)) as (ApiError & T) | null;
  if (res.ok) return responseBody as T;
  throw errorFromEnvelope(responseBody, res);
}

/** Unauthenticated GET for opaque public receipts; never touches operator auth. */
export async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}/api/v1${path}`);
  const body = (await res.json().catch(() => null)) as (ApiError & T) | null;
  if (res.ok) return body as T;
  throw errorFromEnvelope(body, res);
}

export const apiGet = <T>(path: string, init: Pick<RequestInit, 'headers' | 'signal'> = {}) =>
  api<T>(path, init);
export const apiPost = <T>(path: string, body: unknown, headers?: HeadersInit) =>
  api<T>(
    path,
    body instanceof FormData
      ? { method: 'POST', headers, body }
      : { method: 'POST', headers, body: JSON.stringify(body) },
  );
export const apiPatch = <T>(path: string, body: unknown, headers?: HeadersInit) =>
  api<T>(path, { method: 'PATCH', headers, body: JSON.stringify(body) });
export const apiDelete = (path: string, body?: unknown, headers?: HeadersInit) =>
  api<void>(path, {
    method: 'DELETE',
    headers,
    ...(body !== undefined
      ? {
          headers: { 'content-type': 'application/json', ...headers },
          body: JSON.stringify(body),
        }
      : {}),
  });

export type { Page };
